import { Player, Position, TacticalPosition, GameConfig, TrainingFocus } from './entities';
import { mensGameConfig } from '../data/mens/config';
import { womensGameConfig } from '../data/womens/config';

// FIX: Add missing functions that were causing import errors.
export const getPlayerWeeklyWage = (player: Player, leagueName: string, gameType: 'mens' | 'womens', config: GameConfig): number => {
    const leagueIndex = config.LEAGUE_NAMES.indexOf(leagueName);
    const leagueTier = config.LEAGUE_NAMES.length - leagueIndex;
    const skillFactor = Math.pow(player.skill, 2.2);
    const leagueMultiplier = Math.pow(leagueTier, 1.5) * 20;
    const baseWage = (skillFactor / 10) + (leagueMultiplier);

    const wage = Math.round(baseWage / 50) * 50;
    return Math.max(50, wage); // Minimum wage
};

export const calculateExpectedWage = (player: Player, isStarter: boolean, leagueName: string, gameType: 'mens' | 'womens', config: GameConfig): number => {
    const baseWage = getPlayerWeeklyWage(player, leagueName, gameType, config);

    // Performance multiplier: form can adjust wage expectation by up to +/- 15%
    const performanceMultiplier = 1 + (player.form / 20); // form is -3 to +3, so range is 0.85 to 1.15

    // Status multiplier: starters expect more
    const statusMultiplier = isStarter ? 1.15 : 1.0;

    // Potential multiplier for young players
    const potentialMultiplier = (player.age < 24) ? 1 + ((player.potential - player.skill) / 150) : 1.0; // max ~1.3 for huge potential gap

    const expected = baseWage * performanceMultiplier * statusMultiplier * potentialMultiplier;
    
    // round to nearest 50
    return Math.max(50, Math.round(expected / 50) * 50);
}

export const getPlayerValue = (player: Player, leagueName: string, gameType: 'mens' | 'womens', config: GameConfig): number => {
    // Defensive checks for partial player objects
    const skill = player.skill || 10;
    const age = player.age || 25;
    const potential = player.potential || skill + 10;
    const form = player.form || 0;
    const goalsScored = player.goalsScored || 0;
    const assists = player.assists || 0;
    const position = player.position || 'MID';
    const wage = player.wage || 0;
    const potmAwards = player.potmAwards || 0;
    const isPromotedYouth = player.isPromotedYouth || false;
    const gamesPlayed = player.gamesPlayed || 0;

    const leagueIndex = config.LEAGUE_NAMES.indexOf(leagueName);
    const leagueTier = config.LEAGUE_NAMES.length - leagueIndex;

    let value = Math.pow(skill, 2.5) * 100;

    // Potential multiplier for young players
    if (age < 24) {
        value *= (1 + (potential - skill) / 75);
    }

    // Age modifier
    if (age > 29) {
        value *= Math.max(0.1, 1 - (age - 29) * 0.15);
    } else if (age < 21) {
        value *= 1.3;
    }

    // League reputation multiplier
    value *= (1 + (leagueTier - 1) * 0.2);

    if (isPromotedYouth && gamesPlayed < 10) {
        value *= 0.5;
    }

    // Performance-based value adjustments
    // Form impact: good form increases value, poor form decreases it
    const formMultiplier = 1 + (form * 0.03); // -3 to +3 form = 0.91x to 1.09x
    value *= formMultiplier;

    // Recent achievements: POTM awards boost value
    if (potmAwards > 0) {
        value *= (1 + Math.min(potmAwards * 0.05, 0.3)); // Up to +30% for 6+ POTM awards this season
    }

    // Goal contributions for attacking players (weighted by position)
    const totalContributions = goalsScored + assists * 0.7;
    if (totalContributions > 0) {
        const contributionValue = totalContributions * 2000; // Each goal/assist worth ~2k base
        const positionMultiplier = (position === 'FWD' ? 1.0 : position === 'MID' ? 0.7 : 0.4);
        value += contributionValue * positionMultiplier * leagueTier;
    }

    if (wage > 0) {
        value += wage * 52 * 0.5;
    }

    const finalValue = Math.max(5000, Math.round(value / 1000) * 1000);
    return finalValue;
};

export const calculateStarRating = (skill: number, leagueAverageSkill: number): number => {
    const difference = skill - leagueAverageSkill;
    const stars = 2.5 + difference / 8;
    return Math.max(0.5, Math.min(5, stars));
};

export const renderStars = (rating: number): string => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5 ? 1 : 0;
    const emptyStars = 5 - fullStars - halfStar;
    return '⭐'.repeat(fullStars) + '✨'.repeat(halfStar) + '⚫'.repeat(emptyStars);
};

export const calculateStarRatingTier = (rating: number): { label: string, className: string, description: string } => {
    if (rating >= 4.8) return { label: 'SUPERSTAR', className: 'rating-superstar', description: 'One of the very best players in the league (4.8+)' };
    if (rating >= 4.2) return { label: 'STAR', className: 'rating-star', description: 'A genuine star player for this level (4.2 - 4.7)' };
    if (rating >= 3.5) return { label: 'KEY', className: 'rating-key', description: 'A key first-team player (3.5 - 4.1)' };
    if (rating >= 2.5) return { label: 'GOOD', className: 'rating-good', description: 'A good player for this league (2.5 - 3.4)' };
    if (rating >= 1.5) return { label: 'DECENT', className: 'rating-decent', description: 'A decent squad player (1.5 - 2.4)' };
    return { label: 'FRINGE', className: 'rating-fringe', description: 'A fringe player at this level (< 1.5)' };
};

export const calculatePotentialRating = (potential: number): { label: string, className: string, description: string } => {
    if (potential > 90) return { label: 'ELITE', className: 'potential-elite', description: 'Generational Talent (91+)' };
    if (potential > 85) return { label: 'STAR', className: 'potential-star', description: 'World Class Player (86-90)' };
    if (potential > 80) return { label: 'KEY', className: 'potential-key', description: 'Leading Star Player (81-85)' };
    if (potential > 75) return { label: 'GOOD', className: 'potential-good', description: 'Good Squad Player (76-80)' };
    if (potential > 65) return { label: 'DECENT', className: 'potential-decent', description: 'Decent Squad Player (66-75)' };
    return { label: 'AVG', className: 'potential-average', description: 'Average Player (<=65)' };
};

export const MAX_SQUAD_SIZE = 26;

export const SQUAD_SIZE = 22;

export const YOUTH_ACADEMY_LEVELS = [
    { level: 1, name: 'Basic Scouting Network', upgradeCost: 250000, weeklyWages: 2000 },
    { level: 2, name: 'Regional Academy', upgradeCost: 1000000, weeklyWages: 15000 },
    { level: 3, name: 'National Academy', upgradeCost: 3000000, weeklyWages: 50000 },
    { level: 4, name: 'Continental Talent Hub', upgradeCost: 8000000, weeklyWages: 150000 },
    { level: 5, name: 'Global Youth Programme', upgradeCost: 20000000, weeklyWages: 400000 },
    { level: 6, name: 'World Class Development Centre', upgradeCost: 40000000, weeklyWages: 800000 },
];

export const TRAINING_FACILITIES_LEVELS = [
    { level: 1, name: 'Basic Pitch', upgradeCost: 100000, weeklyWages: 5000 },
    { level: 2, name: 'Standard Complex', upgradeCost: 750000, weeklyWages: 25000 },
    { level: 3, name: 'Advanced Centre', upgradeCost: 3000000, weeklyWages: 80000 },
    { level: 4, name: 'Elite Facility', upgradeCost: 10000000, weeklyWages: 200000 },
    { level: 5, name: 'State-of-the-Art Campus', upgradeCost: 25000000, weeklyWages: 500000 },
    { level: 6, name: 'World Class Performance Hub', upgradeCost: 50000000, weeklyWages: 1000000 },
];

export const ASSISTANT_MANAGER_LEVELS = [
    { level: 1, name: 'Aspiring Tactician', upgradeCost: 150000, weeklyWages: 3000, accuracy: 0.6 },
    { level: 2, name: 'Experienced Coach', upgradeCost: 600000, weeklyWages: 12000, accuracy: 0.7 },
    { level: 3, name: 'Seasoned Strategist', upgradeCost: 2000000, weeklyWages: 35000, accuracy: 0.8 },
    { level: 4, name: 'Elite Tactician', upgradeCost: 6000000, weeklyWages: 80000, accuracy: 0.9 },
    { level: 5, name: 'World Class Manager', upgradeCost: 15000000, weeklyWages: 200000, accuracy: 0.95 },
    { level: 6, name: 'Legendary Strategic Mind', upgradeCost: 30000000, weeklyWages: 400000, accuracy: 1.0 },
];

// FIX: Centralize training focus options to be used across components.
export const TRAINING_FOCUS_OPTIONS: TrainingFocus[] = ['General', 'Fitness', 'Rest', 'Goalkeeping', 'Defending', 'Midfield', 'Attacking'];

// Individual player attribute training options (available at training facility level 3+)
export const INDIVIDUAL_PLAYER_ATTRIBUTES = ['Pace', 'Passing', 'Tackling', 'Marking', 'Heading', 'Dribbling', 'Finishing', 'Creativity', 'Positioning', 'Handling', 'Reflexes'];

export const ATTRIBUTES: Record<Position, string[]> = {
    GK: ['handling', 'reflexes', 'positioning'],
    DEF: ['tackling', 'marking', 'heading', 'positioning'],
    MID: ['passing', 'dribbling', 'creativity', 'tackling'],
    FWD: ['finishing', 'dribbling', 'pace', 'heading']
};

export const FORMATION_OPTIONS = ['4-4-2', '4-3-3', '3-5-2', '5-3-2'];

export const FORMATIONS: Record<string, TacticalPosition[]> = {
    '4-4-2': ['GK', 'LB', 'LCB', 'RCB', 'RB', 'LM', 'LCM', 'RCM', 'RM', 'ST (L)', 'ST (R)'],
    '4-3-3': ['GK', 'LB', 'LCB', 'RCB', 'RB', 'LCM', 'CM', 'RCM', 'LW', 'ST', 'RW'],
    '3-5-2': ['GK', 'LCB', 'CB', 'RCB', 'LM', 'LCM', 'CM', 'RCM', 'RM', 'ST (L)', 'ST (R)'],
    '5-3-2': ['GK', 'LWB', 'LCB', 'CB', 'RCB', 'RWB', 'LCM', 'CM', 'RCM', 'ST (L)', 'ST (R)'],
};

export const POSITION_MAP: Record<TacticalPosition, Position> = {
    'GK': 'GK',
    'LB': 'DEF', 'LCB': 'DEF', 'CB': 'DEF', 'RCB': 'DEF', 'RB': 'DEF', 'LWB': 'DEF', 'RWB': 'DEF',
    'LM': 'MID', 'LCM': 'MID', 'CM': 'MID', 'RCM': 'MID', 'RM': 'MID', 'CAM': 'MID',
    'LW': 'FWD', 'ST': 'FWD', 'RW': 'FWD', 'ST (L)': 'FWD', 'ST (R)': 'FWD',
};

export const TACTICAL_POSITION_DETAILS: Record<TacticalPosition, { category: 'Central' | 'Wide', side: 'Left' | 'Right' | 'Center' }> = {
    'GK': { category: 'Central', side: 'Center' },
    // 4-4-2
    'LB': { category: 'Wide', side: 'Left' },
    'LCB': { category: 'Central', side: 'Left' },
    'RCB': { category: 'Central', side: 'Right' },
    'RB': { category: 'Wide', side: 'Right' },
    'LM': { category: 'Wide', side: 'Left' },
    'LCM': { category: 'Central', side: 'Left' },
    'RCM': { category: 'Central', side: 'Right' },
    'RM': { category: 'Wide', side: 'Right' },
    'ST (L)': { category: 'Central', side: 'Left' },
    'ST (R)': { category: 'Central', side: 'Right' },
    // 4-3-3
    'CM': { category: 'Central', side: 'Center' },
    'LW': { category: 'Wide', side: 'Left' },
    'ST': { category: 'Central', side: 'Center' },
    'RW': { category: 'Wide', side: 'Right' },
    // 3-5-2
    'CB': { category: 'Central', side: 'Center' },
    // 5-3-2
    'LWB': { category: 'Wide', side: 'Left' },
    'RWB': { category: 'Wide', side: 'Right' },
    // Extra for flexibility
    'CAM': { category: 'Central', side: 'Center' },
};

export const TACTICAL_ATTRIBUTES: Record<string, { key: string[], secondary: string[] }> = {
    GK: { key: ['handling', 'reflexes', 'positioning'], secondary: [] },
    DEF: { key: ['tackling', 'marking', 'positioning'], secondary: ['heading', 'pace'] },
    MID: { key: ['passing', 'creativity', 'positioning'], secondary: ['tackling', 'dribbling'] },
    FWD: { key: ['finishing', 'dribbling', 'pace'], secondary: ['heading', 'positioning'] },
    CB: { key: ['tackling', 'marking', 'heading', 'positioning'], secondary: ['pace'] },
    LCB: { key: ['tackling', 'marking', 'heading', 'positioning'], secondary: ['pace'] },
    RCB: { key: ['tackling', 'marking', 'heading', 'positioning'], secondary: ['pace'] },
    LB: { key: ['tackling', 'marking', 'pace'], secondary: ['positioning', 'dribbling', 'passing'] },
    RB: { key: ['tackling', 'marking', 'pace'], secondary: ['positioning', 'dribbling', 'passing'] },
    LWB: { key: ['pace', 'dribbling', 'passing'], secondary: ['tackling', 'creativity'] },
    RWB: { key: ['pace', 'dribbling', 'passing'], secondary: ['tackling', 'creativity'] },
    CM: { key: ['passing', 'creativity', 'positioning'], secondary: ['tackling', 'dribbling'] },
    LCM: { key: ['passing', 'creativity', 'positioning'], secondary: ['tackling', 'dribbling'] },
    RCM: { key: ['passing', 'creativity', 'positioning'], secondary: ['tackling', 'dribbling'] },
    LM: { key: ['dribbling', 'pace', 'passing'], secondary: ['creativity', 'finishing'] },
    RM: { key: ['dribbling', 'pace', 'passing'], secondary: ['creativity', 'finishing'] },
    CAM: { key: ['creativity', 'passing', 'dribbling'], secondary: ['finishing', 'pace'] },
    ST: { key: ['finishing', 'positioning', 'pace'], secondary: ['heading', 'dribbling'] },
    'ST (L)': { key: ['finishing', 'positioning', 'pace'], secondary: ['heading', 'dribbling'] },
    'ST (R)': { key: ['finishing', 'positioning', 'pace'], secondary: ['heading', 'dribbling'] },
    LW: { key: ['pace', 'dribbling', 'finishing'], secondary: ['creativity', 'passing'] },
    RW: { key: ['pace', 'dribbling', 'finishing'], secondary: ['creativity', 'passing'] },
};


export const getFormationZones = (formation: string): TacticalPosition[][] => {
    const positions = FORMATIONS[formation];
    if (!positions) return [];

    const zones: Record<string, TacticalPosition[]> = {
        'GK': [],
        'DEF': [],
        'MID': [],
        'FWD': []
    };

    positions.forEach(pos => {
        const generalPos = POSITION_MAP[pos];
        if (generalPos === 'GK') zones['GK'].push(pos);
        else if (generalPos === 'DEF') zones['DEF'].push(pos);
        else if (generalPos === 'MID') zones['MID'].push(pos);
        else if (generalPos === 'FWD') zones['FWD'].push(pos);
    });

    return Object.values(zones).filter(zone => zone.length > 0);
};

// FIX: Exported game configuration constants to fix import errors across the app.
export const LEAGUE_CONFIG = mensGameConfig.LEAGUE_CONFIG;
export const WOMEN_LEAGUE_CONFIG = womensGameConfig.LEAGUE_CONFIG;
export const LEAGUE_NAMES = mensGameConfig.LEAGUE_NAMES;
export const WOMEN_LEAGUE_NAMES = womensGameConfig.LEAGUE_NAMES;
export const MENS_WEEKS_PER_SEASON = mensGameConfig.WEEKS_PER_SEASON;
export const WOMENS_WEEKS_PER_SEASON = womensGameConfig.WEEKS_PER_SEASON;
export const CUP_NAMES = mensGameConfig.CUP_NAMES;
export const WOMEN_CUP_NAMES = womensGameConfig.CUP_NAMES;

// Team badge icons - available badges for team visual identity
// Each badge is a Font Awesome icon name that will be displayed in team's primary color
export const TEAM_BADGE_ICONS = [
    'crown',           // Royal/elite badge
    'medal',           // Achievement/honor
    'shield',          // Protection/defense
    'hexagon',         // Geometric badge
    'clover',          // Luck/fortune
    'gem',             // Precious/valuable
    'star',            // Excellence
    'feather',         // Swift/agile
    'wand-magic-sparkles', // Magic/special
    'fire',            // Fire/aggressive
    'snowflake',       // Ice/cool
    'mountain',        // Strength/elevation
    'trophy',          // Championship
    'ribbon',          // Banner/ribbon
    'diamond',         // Diamond/precious
    'bolt',            // Power/speed
    'vial',            // Essence/core
    'leaf',            // Nature/growth
    'moon',            // Night/mysterious
    'sun',             // Day/bright
    'egg',             // New/potential
    'hourglass-end',   // Time/experience
    'dice',            // Chance/unpredictable
    'wind',            // Fast/air
    'tornado',         // Turbulent/powerful
    'cloud',           // Sky/high
    'house',           // Home/base
    'bridge',          // Connection/unite
    'drum',            // Rhythm/beat
    'guitar',          // Music/style
    'lightbulb',       // Innovation/bright
    'key',             // Important/unlock
    'lock',            // Protection/secure
    'anchor',          // Stability/steady
    'sailboat',        // Adventure/journey
    'code',            // Precision/skill
    'flask',           // Experiment/alchemy
    'palette',         // Creativity/art
    'pen-fancy',       // Style/refinement
    'chess',           // Strategy/intellect
    'landmark',        // Heritage/tradition
    'lifering',        // Safety/rescue
];

/**
 * Check if a player is in transfer cooldown
 * Players cannot be transferred for 30 weeks (6 months of a 60-week season) after their last transfer
 */
export const isPlayerInTransferCooldown = (player: Player, currentSeason: number, currentWeek: number): boolean => {
    if (!player.lastTransferredAt) {
        return false; // No previous transfer, not in cooldown
    }

    const WEEKS_COOLDOWN = 30; // 6 months = 30 weeks (half of 60-week season)
    const weeksPerSeason = 60;

    let weeksElapsed = 0;

    if (currentSeason === player.lastTransferredAt.season) {
        // Same season
        weeksElapsed = currentWeek - player.lastTransferredAt.week;
    } else {
        // Different season
        const seasonsDifference = currentSeason - player.lastTransferredAt.season;
        const weeksRemainingInTransferSeason = weeksPerSeason - player.lastTransferredAt.week;
        weeksElapsed = weeksRemainingInTransferSeason + (currentWeek) + ((seasonsDifference - 1) * weeksPerSeason);
    }

    return weeksElapsed < WEEKS_COOLDOWN;
};

/**
 * Calculate release penalty based on player market value
 * Penalty is calculated as a percentage of player's market value (e.g., 30% of market value)
 */
export const calculateReleasePenalty = (player: Player, leagueName: string, gameType: 'mens' | 'womens', config: GameConfig): number => {
    const playerValue = getPlayerValue(player, leagueName, gameType, config);
    // Release penalty is 30% of player's market value
    const RELEASE_PENALTY_PERCENT = 0.30;
    return Math.round(playerValue * RELEASE_PENALTY_PERCENT);
};